<template>
  <div class="double-refraction-pro-viz">
    <div class="viz-header-compact" >
       <div class="brief" v-latex="knowledgePoint"></div>
       <div class="formula-chip" v-latex>$n_o ≠ n_e | 寻常光(o) & 非常光(e)$</div>
    </div>

    <div class="split-layout">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage flex-1" style="flex: 1.2;">
        <svg width="100%" height="380" viewBox="0 0 800 600" preserveAspectRatio="xMidYMid meet">
           <defs>
              <filter id="glow-dr"><feGaussianBlur stdDeviation="3" result="blur" /><feComposite in="SourceGraphic" in2="blur" operator="over" /></filter>
              <linearGradient id="crystal-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                 <stop offset="0%" stop-color="rgba(255,255,255,0.05)" />
                 <stop offset="50%" stop-color="rgba(255,255,255,0.15)" />
                 <stop offset="100%" stop-color="rgba(255,255,255,0.05)" />
              </linearGradient>
           </defs>

           <!-- Grid Background -->
           <g stroke="rgba(241, 245, 249, 0.03)" stroke-width="2">
              <line v-for="i in 17" :key="'v'+i" :x1="i*50" y1="0" :x2="i*50" y2="600" />
              <line v-for="i in 13" :key="'h'+i" x1="0" :y1="i*50" x2="800" :y2="i*50" />
           </g>

           <!-- Laser Source -->
           <rect x="20" y="270" width="40" height="60" fill="#334155" rx="4" />
           <rect x="60" y="290" width="10" height="20" fill="#cbd5e1" />
           
           <!-- Incident Light Ray (Natural Light, Unpolarized or mixed) -->
           <line x1="70" y1="300" x2="250" y2="300" stroke="#facc15" stroke-width="4" filter="url(#glow-dr)" />
           <!-- Natural Light Indicators (Polarization in all directions, simple cross here) -->
           <g transform="translate(150, 300)">
              <line x1="0" y1="-15" x2="0" y2="15" stroke="#fff" stroke-width="2" />
              <circle r="4" fill="#fff" />
              <text x="0" y="-25" fill="#facc15" font-size="14" font-weight="900" text-anchor="middle">自然光 (非偏振)</text>
           </g>

           <!-- Calcite Crystal (Iceland Spar) -->
           <g :transform="`translate(250, 150) rotate(${rotAngle}, 150, 150)`">
              <polygon points="50,0 350,0 300,300 0,300" fill="url(#crystal-grad)" stroke="rgba(255,255,255,0.3)" stroke-width="3" />
              <text x="150" y="40" fill="rgba(255,255,255,0.2)" font-size="28" font-weight="900" text-anchor="middle">方解石晶体 (CaCO₃)</text>
           </g>

           <!-- Inside Crystal - Rays separate -->
           <g>
              <!-- Ordinary Ray (o-ray) - Straight through -->
              <line x1="250" y1="300" x2="550" y2="300" stroke="#facc15" stroke-width="2" opacity="0.6">
                 <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" />
              </line>
              
              <!-- Extraordinary Ray (e-ray) - Deflected based on rotation angle -->
              <line x1="250" y1="300" :x2="550" :y2="300 + eRayOffset" stroke="#38bdf8" stroke-width="2" opacity="0.6">
                 <animate attributeName="opacity" values="0.4;1;0.4" dur="1.2s" repeatCount="indefinite" />
              </line>
           </g>

           <!-- Transmitted Rays & Detectors -->
           <g>
               <!-- O-ray exit -->
               <line x1="550" y1="300" x2="700" y2="300" stroke="#facc15" stroke-width="4" filter="url(#glow-dr)" />
               <text x="625" y="280" fill="#facc15" font-size="16" font-weight="900" text-anchor="middle">o光 (寻常光)</text>
               <!-- O-ray Polarization (Perpendicular to page, represented by dots) -->
               <g transform="translate(625, 300)">
                  <circle r="6" fill="#facc15" stroke="#fff" stroke-width="2" />
               </g>

               <!-- E-ray exit -->
               <line x1="550" :y1="300 + eRayOffset" x2="700" :y2="300 + eRayOffset" stroke="#38bdf8" stroke-width="4" filter="url(#glow-dr)" />
               <text x="625" :y="325 + eRayOffset" fill="#38bdf8" font-size="16" font-weight="900" text-anchor="middle">e光 (非常光)</text>
               <!-- E-ray Polarization (Parallel to page plane, represented by vertical arrow) -->
               <g transform="translate(625, 300 + eRayOffset)">
                  <line x1="0" y1="-15" x2="0" y2="15" stroke="#fff" stroke-width="3" />
               </g>
           </g>

           <!-- Imaging Screen/Eye -->
           <line x1="720" y1="100" x2="720" y2="500" stroke="#475569" stroke-width="6" />
           <text x="735" y="300" fill="#94a3b8" font-size="20" font-weight="900" writing-mode="vertical-rl" transform="rotate(180, 735, 300)">观 察 屏 面</text>
        </svg>

      </div>

      <!-- 右侧解析面板 -->
      <div class="simulation-stage logic-panel">
         <div class="logic-header">双折射光学分解</div>
         <div class="flex-column-center" style="flex:1; padding: 15px;">
            
            <div class="eq-box text-center">
               <div class="eq-display text-blue">当前光轴状态参数计算</div>
               <div class="seq-data-list">
                  <div class="data-row"><span>晶体旋转角度 θ</span><span class="hl">{{ rotAngle }}°</span></div>
                  <div class="data-row"><span>o光折射率 (n_o)</span><span class="hl text-yellow">1.658 (恒定)</span></div>
                  <div class="data-row"><span>e光折射率 (n_e_eff)</span><span class="hl text-blue">{{ neEffective.toFixed(4) }} (视向变化)</span></div>
                  <div class="data-row" style="background: rgba(56,189,248,0.1)">
                     <span>像平面分裂距离 Δ</span><span class="hl text-blue">{{ Math.abs(eRayOffset).toFixed(1) }} px</span>
                  </div>
               </div>
            </div>

            <div class="eq-box mt-l">
               <p class="desc-text" style="font-size: 13px;"><strong>核心洞察：</strong><br/><br/>一束光射入各向异性晶体内部时会分裂成两束**线偏振光**：<br/><br/>
               **寻常光(o光)**：符合普通折射定律，光速在各个方向均相同。<br/><br/>
               **非常光(e光)**：不一定在入射面内，光速随方向改变而改变！其偏振方向与o光严格垂直。</p>
            </div>
         </div>
      </div>
    </div>

    <!-- 控制台 -->
    <div class="compact-controls">
      <div class="control-card">
         <div class="control-grid-mini">
            <div class="s-item" style="grid-column: span 2;">
               <label>旋转晶体角度 (绕入射光轴)</label>
               <el-slider v-model="rotAngle" :min="0" :max="360" :step="1" size="small"></el-slider>
            </div>
            
            <div class="s-item">
               <label>晶体厚度 (物理分裂尺度缩放)</label>
               <el-slider v-model="crystalThickness" :min="1" :max="3" :step="0.1" size="small"></el-slider>
            </div>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DoubleRefractionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      rotAngle: 30,
      crystalThickness: 1.5,
      // true refractive indices for Calcite are roughly 1.658 (o) and 1.486 (e)
      no: 1.658,
      ne: 1.486 
    }
  },
  computed: {
    // effective refractive index for e-ray depends heavily on angle w.r.t optic axis
    neEffective() {
       const rad = this.rotAngle * Math.PI / 180;
       return 1 / Math.sqrt( Math.pow(Math.cos(rad)/this.no, 2) + Math.pow(Math.sin(rad)/this.ne, 2) );
    },
    eRayOffset() {
       // Mock physics logic for offset driven by angle relative to optic axis
       const rad = this.rotAngle * Math.PI / 180;
       return 80 * this.crystalThickness * Math.sin(rad); 
    }
  }
}
</script>

<style scoped lang="scss">
.double-refraction-pro-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow-y: auto; overflow-x: hidden; padding-right: 5px; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 15px; color: #1e293b; font-weight: 700; } .formula-chip { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; padding: 8px 18px; border-radius: 6px; font-weight: 900; font-family: -apple-system, sans-serif; font-size: 18px; } }
.split-layout { display: flex; gap: 12px; flex: 1; min-height: 360px; }
.flex-1 { flex: 1; min-width: 0; }
.logic-panel { flex: 0.8; min-width: 0; display: flex; flex-direction: column; background: #0f172a !important; border-radius: 12px; }
.logic-header { background: #1e293b; padding: 12px; color: #38bdf8; font-size: 14px; font-weight: 900; text-align: center; border-bottom: 2px solid #0f172a; border-radius: 12px 12px 0 0; }
.flex-column-center { display: flex; flex-direction: column; justify-content: flex-start; overflow-y: auto; }

.eq-box { width: 100%; border-radius: 8px; overflow: hidden; }
.eq-display { background: rgba(0,0,0,0.4); padding: 10px; font-weight: 900; border-bottom: 1px solid rgba(255,255,255,0.05); }
.seq-data-list { display: flex; flex-direction: column; background: rgba(15, 23, 42, 0.6); }
.data-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 18px; border-bottom: 1px solid rgba(255,255,255,0.02); color: #94a3b8; font-size: 13px; font-weight: bold; }
.hl { color: #f8fafc; font-weight: 900; font-family: monospace; font-size: 14px; }

.text-yellow { color: #facc15; } .text-blue { color: #38bdf8; }
.desc-text { color: #cbd5e1; font-size: 13px; line-height: 1.6; padding: 16px; background: rgba(56, 189, 248, 0.05); border-radius: 8px; border: 1px solid rgba(56, 189, 248, 0.1); }
.mt-l { margin-top: 25px; }

.simulation-stage { background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; }

.compact-controls { flex: 0; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: #ffffff; padding: 16px 20px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-grid-mini { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 15px; .s-item { label { font-size: 13px; color: #475569; font-weight: 900; margin-bottom: 8px; display: block; } } }
</style>
